import { Link } from "wouter";
import GuideShell from "@/components/GuideShell";

import React from "react";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  cream: "oklch(0.98 0.008 85)",
  linen: "oklch(0.93 0.01 85)",
  espresso: "oklch(0.22 0.015 65)",
  terracotta: "oklch(0.32 0.08 255)",
  terracottaLight: "oklch(0.32 0.08 255 / 0.08)",
  terracottaMid: "oklch(0.32 0.08 255 / 0.22)",
  border: "oklch(0.88 0.012 85)",
  white: "oklch(1 0.004 85)",
  muted: "oklch(0.52 0.015 65)",
  body: "oklch(0.52 0.015 65)",
};

const WINS = [
  {
    title: "How to use Claude for calm, thoughtful iteration instead of rapid expansion",
    whenHelps: "When you're drafting something important and want to refine it carefully",
    steps: [
      "Open a new conversation with Claude",
      "Share your draft and ask: 'Help me refine this for clarity and tone'",
      "Review Claude's suggestions and iterate together",
      "Use the feedback to strengthen your thinking",
    ],
    principle: "Deliberate Thinking: Slow down to think better.",
  },
  {
    title: "How to preserve your voice while getting structural feedback",
    whenHelps: "When you need feedback but want to maintain your unique perspective",
    steps: [
      "Share your work with Claude and say: 'Give me structural feedback, but preserve my voice'",
      "Ask Claude to highlight what's working in your original voice",
      "Integrate feedback while keeping your authentic tone",
      "Test the revised version to ensure it still sounds like you",
    ],
    principle: "Voice Preservation: Keep your perspective intact while improving.",
  },
  {
    title: "How to slow the process on purpose when decisions feel complex",
    whenHelps: "When you're facing a decision with high stakes or multiple considerations",
    steps: [
      "Ask Claude to break down the decision into components",
      "Explore each component separately before deciding",
      "Use Claude to stress-test your thinking",
      "Take time to sit with the analysis before deciding",
    ],
    principle: "Deliberate Thinking: Complexity deserves careful consideration.",
  },
  {
    title: "How to use Claude's new Projects and Customization features for deeper thinking",
    whenHelps: "When you're working on ongoing projects that require consistent context",
    steps: [
      "Create a new Project in Claude for your work",
      "Upload relevant documents and context",
      "Customize Claude's behavior for your specific needs",
      "Reference the project across multiple conversations",
    ],
    principle: "Structured Thinking: Use tools to maintain context and consistency.",
  },
  {
    title: "How to ask Claude for multiple perspectives on complex topics",
    whenHelps: "When you need to understand different viewpoints before deciding",
    steps: [
      "Ask Claude to present 3 different perspectives on your question",
      "Explore the strengths and weaknesses of each view",
      "Identify which perspective aligns with your values",
      "Use the analysis to inform your decision",
    ],
    principle: "Perspective Taking: Understand complexity through multiple lenses.",
  },
  {
    title: "How to use Claude to stress-test your ideas before sharing them",
    whenHelps: "When you want to strengthen your argument before presenting it",
    steps: [
      "Share your idea with Claude and ask: 'What's the strongest counterargument?'",
      "Listen to Claude's critique without defending",
      "Strengthen your idea based on the feedback",
      "Share the improved version with confidence",
    ],
    principle: "Deliberate Thinking: Test your ideas to make them stronger.",
  },
  {
    title: "How to use Claude for research that actually informs your thinking",
    whenHelps: "When you need to research a topic but want to go deeper than surface-level",
    steps: [
      "Start with a clear research question",
      "Ask Claude to help you explore the question systematically",
      "Dig into the nuances and implications",
      "Synthesize what you've learned into actionable insights",
    ],
    principle: "Structured Thinking: Research with purpose and depth.",
  },
  {
    title: "How to use Claude to clarify your own thinking before writing",
    whenHelps: "When you're not sure what you think yet, but need to write about it",
    steps: [
      "Tell Claude what you're trying to figure out",
      "Let Claude ask you clarifying questions",
      "Answer the questions to discover what you actually think",
      "Now you're ready to write from a clear perspective",
    ],
    principle: "Deliberate Thinking: Clarity comes before good writing.",
  },
  {
    title: "How to use Claude's memory features to maintain continuity across projects",
    whenHelps: "When you're working on long-term projects that span multiple conversations",
    steps: [
      "Use Claude's memory to save key context and preferences",
      "Reference saved context in new conversations",
      "Build on previous work without re-explaining everything",
      "Maintain consistency across your projects",
    ],
    principle: "Structured Thinking: Continuity strengthens long-term work.",
  },
  {
    title: "How to use Claude to generate options when you're stuck",
    whenHelps: "When you're facing a problem and need fresh ideas",
    steps: [
      "Describe your constraint or problem clearly",
      "Ask Claude for 5-10 different approaches",
      "Evaluate each option against your criteria",
      "Choose the one that fits best, or combine elements",
    ],
    principle: "Creative Thinking: Options come from structured exploration.",
  },
  {
    title: "How to use Claude for accountability without judgment",
    whenHelps: "When you need someone to help you think through your progress",
    steps: [
      "Share your goals and progress with Claude",
      "Ask Claude to help you assess what's working",
      "Explore what's getting in the way",
      "Identify your next step with clarity",
    ],
    principle: "Deliberate Thinking: Progress comes from honest reflection.",
  },
  {
    title: "How to use Claude to prepare for difficult conversations",
    whenHelps: "When you need to have a conversation that matters and want to prepare",
    steps: [
      "Describe the conversation you need to have",
      "Ask Claude to help you think through different scenarios",
      "Practice your key points",
      "Go into the conversation with clarity and confidence",
    ],
    principle: "Deliberate Thinking: Preparation leads to better conversations.",
  },
  {
    title: "How to use Claude's new thinking mode for complex problem-solving",
    whenHelps: "When you're facing a complex problem that needs deep analysis",
    steps: [
      "Use Claude's extended thinking mode for your question",
      "Let Claude work through the problem systematically",
      "Review Claude's reasoning process",
      "Apply the insights to your situation",
    ],
    principle: "Structured Thinking: Deep analysis reveals better solutions.",
  },
  {
    title: "How to use Claude to turn feedback into growth",
    whenHelps: "When you've received feedback and want to understand how to use it",
    steps: [
      "Share the feedback with Claude",
      "Ask Claude to help you understand what it means",
      "Explore how you can apply it to improve",
      "Create a plan for implementing the feedback",
    ],
    principle: "Deliberate Thinking: Feedback is a gift when you know how to use it.",
  },
];

export default function ClaudeQuickWinsDelivery() {
  const [expandedIndices, setExpandedIndices] = React.useState<number[]>([0]);

  const toggleExpand = (index: number) => {
    setExpandedIndices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  return (
    /* Two headers used to stack here: GuideShell's, and a hand-rolled sticky
       strip left over from when this page rendered on its own. Both were
       position:sticky, so scrolling pinned two bars to the top of a phone
       screen and the second one carried a different wordmark, a different type
       scale and no way back. The strip is gone; its context label and its one
       link are expressed through the shell instead. */
    <GuideShell context="Quick Wins · Claude" backHref="/resources/claude" backLabel="Back to Claude">
      {/* Main Content */}
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "3rem 2rem" }}>
        <div style={{ marginBottom: "3rem" }}>
          <p style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600, color: C.muted, marginBottom: "1rem" }}>
            Your 14 Quick Wins
          </p>
          <h1 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "clamp(32px, 5vw, 44px)", fontWeight: 700, color: C.espresso, lineHeight: 1.2, marginBottom: "1rem" }}>
            You're ready to explore Claude like a thinking partner.
          </h1>
          <p style={{ fontSize: 16, color: C.body, lineHeight: 1.8 }}>
            Here are 14 Quick Wins that show you exactly how to use Claude as a deliberate thinking partner. Click each to explore.
          </p>
        </div>

        {/* Quick Wins Grid */}
        <div style={{ display: "grid", gap: "1rem", marginBottom: "3rem" }}>
          {WINS.map((win, index) => (
            <div
              key={index}
              style={{
                background: C.white,
                border: `1px solid ${C.border}`,
                borderRadius: 12,
                overflow: "hidden",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onClick={() => toggleExpand(index)}
            >
              <div style={{ padding: "1.5rem", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem" }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600, color: C.terracotta, marginBottom: "0.5rem" }}>
                    Win {index + 1}
                  </p>
                  <h3 style={{ fontSize: 16, fontWeight: 600, color: C.espresso, lineHeight: 1.5, margin: 0 }}>
                    {win.title}
                  </h3>
                </div>
                <div style={{ fontSize: 20, color: C.terracotta, flexShrink: 0, marginTop: "0.25rem" }}>
                  {expandedIndices.includes(index) ? "−" : "+"}
                </div>
              </div>

              {expandedIndices.includes(index) && (
                <div style={{ padding: "0 1.5rem 1.5rem", borderTop: `1px solid ${C.border}`, paddingTop: "1.5rem" }}>
                  <div style={{ marginBottom: "1.5rem" }}>
                    <p style={{ fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600, color: C.muted, marginBottom: "0.5rem" }}>
                      When This Helps
                    </p>
                    <p style={{ fontSize: 14, color: C.body, lineHeight: 1.6, margin: 0 }}>
                      {win.whenHelps}
                    </p>
                  </div>

                  <div style={{ marginBottom: "1.5rem" }}>
                    <p style={{ fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600, color: C.muted, marginBottom: "0.75rem" }}>
                      How
                    </p>
                    <ol style={{ fontSize: 14, color: C.body, lineHeight: 1.6, margin: 0, paddingLeft: "1.5rem" }}>
                      {win.steps.map((step, i) => (
                        <li key={i} style={{ marginBottom: "0.5rem" }}>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>

                  <div style={{ background: C.terracottaLight, border: `1px solid ${C.border}`, borderRadius: 8, padding: "1rem", marginTop: "1rem" }}>
                    <p style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600, color: C.muted, marginBottom: "0.5rem" }}>
                      Smart Stack Principle
                    </p>
                    <p style={{ fontSize: 14, fontWeight: 600, color: C.espresso, margin: 0 }}>
                      {win.principle}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div style={{ background: C.espresso, borderRadius: 18, padding: "3rem 2.5rem", textAlign: "center", color: C.white }}>
          <h2 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 32, fontWeight: 700, marginBottom: "1rem" }}>
            Ready to go deeper?
          </h2>
          <p style={{ fontSize: 16, lineHeight: 1.6, marginBottom: "2rem", color: C.border }}>
            These 14 Quick Wins are just the beginning. Get access to the full Claude resource library with strategies, templates, and advanced techniques.
          </p>
          <Link
            href="/auth"
            style={{
              display: "inline-block",
              background: C.terracotta,
              color: C.white,
              padding: "14px 28px",
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              textDecoration: "none",
              transition: "background 0.2s ease",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "oklch(0.26 0.075 255)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = C.terracotta; }}
          >
            Explore the Full Claude Resource Library →
          </Link>
        </div>
      </div>
    </GuideShell>
  );
}
